import api from '../utils/api';
import { cachedRequest, invalidateReference } from '../utils/referenceCache';

// Run a mutation, then invalidate the cached reference key(s) it affects so the
// next read reflects the change immediately instead of waiting out the TTL.
const bust = (prefixes, promise) =>
  promise.then((r) => { [].concat(prefixes).forEach((p) => invalidateReference(p)); return r; });

export const inventoryService = {
  // Item Groups
  getItemGroups: () => cachedRequest('inv:groups', () => api.get('/inventory/groups')),
  createItemGroup: (data) => bust('inv:groups', api.post('/inventory/groups', data)),
  updateItemGroup: (id, data) => bust('inv:groups', api.put(`/inventory/groups/${id}`, data)),
  deleteItemGroup: (id) => bust('inv:groups', api.delete(`/inventory/groups/${id}`)),

  // UOM
  getUOMs: () => cachedRequest('inv:uoms', () => api.get('/inventory/uom')),
  createUOM: (data) => bust('inv:uoms', api.post('/inventory/uom', data)),
  updateUOM: (id, data) => bust('inv:uoms', api.put(`/inventory/uom/${id}`, data)),
  deleteUOM: (id) => bust('inv:uoms', api.delete(`/inventory/uom/${id}`)),
  getUOMConversions: () => cachedRequest('inv:uomconv', () => api.get('/inventory/uom/conversions')),
  createUOMConversion: (data) => bust('inv:uomconv', api.post('/inventory/uom/conversions', data)),
  updateUOMConversion: (id, data) => bust('inv:uomconv', api.put(`/inventory/uom/conversions/${id}`, data)),
  deleteUOMConversion: (id) => bust('inv:uomconv', api.delete(`/inventory/uom/conversions/${id}`)),

  // Items
  getItems: (params) => cachedRequest(`inv:items:${JSON.stringify(params || {})}`, () => api.get('/inventory/items', { params })),
  getItem: (id) => api.get(`/inventory/items/${id}`),
  getResolvedSellingPrice: (id, params) => api.get(`/inventory/items/${id}/resolved-selling-price`, { params }),
  createItem: (data) => bust('inv:items', api.post('/inventory/items', data)),
  updateItem: (id, data) => bust('inv:items', api.put(`/inventory/items/${id}`, data)),
  setItemActiveStatus: (id, isActive) => bust('inv:items', api.put(`/inventory/items/${id}/active-status`, { is_active: isActive })),
  getItemKitComponents: (id) => api.get(`/inventory/items/${id}/kit-components`),
  setItemKitComponents: (id, lines) => api.put(`/inventory/items/${id}/kit-components`, { lines }),
  deleteVariantItem: (id) => bust('inv:items', api.delete(`/inventory/items/${id}/variant`)),
  deleteItem: (id) => bust('inv:items', api.delete(`/inventory/items/${id}`)),

  // Price Lists
  getPriceLists: () => cachedRequest('inv:pricelists', () => api.get('/inventory/price-lists')),
  createPriceList: (data) => bust('inv:pricelists', api.post('/inventory/price-lists', data)),
  updatePriceList: (id, data) => bust('inv:pricelists', api.put(`/inventory/price-lists/${id}`, data)),
  deletePriceList: (id) => bust('inv:pricelists', api.delete(`/inventory/price-lists/${id}`)),
  getPriceListItems: (id) => api.get(`/inventory/price-lists/${id}/items`),
  setPriceListItem: (id, data) => api.post(`/inventory/price-lists/${id}/items`, data),
  setPriceListItemsBatch: (id, items) => api.post(`/inventory/price-lists/${id}/items/batch`, { items }),
  deletePriceListItem: (plId, itemId) => api.delete(`/inventory/price-lists/${plId}/items/${itemId}`),
  getPriceImportBatches: () => api.get('/inventory/price-list-import-batches'),
  createPriceImportBatch: (data) => api.post('/inventory/price-list-import-batches', data),
  getPriceImportBatch: (id) => api.get(`/inventory/price-list-import-batches/${id}`),
  replacePriceImportBatchLines: (id, rows) => api.post(`/inventory/price-list-import-batches/${id}/lines`, { rows }),
  applyPriceImportBatch: (id) => api.post(`/inventory/price-list-import-batches/${id}/apply`),

  // Locations & Warehouses
  getLocations: () => cachedRequest('inv:locations', () => api.get('/inventory/locations')),
  createLocation: (data) => bust('inv:locations', api.post('/inventory/locations', data)),
  updateLocation: (id, data) => bust('inv:locations', api.put(`/inventory/locations/${id}`, data)),
  deleteLocation: (id) => bust('inv:locations', api.delete(`/inventory/locations/${id}`)),
  getWarehouses: () => cachedRequest('inv:warehouses', () => api.get('/inventory/warehouses')),
  createWarehouse: (data) => bust(['inv:warehouses', 'inv:itemwh'], api.post('/inventory/warehouses', data)),
  updateWarehouse: (id, data) => bust(['inv:warehouses', 'inv:itemwh'], api.put(`/inventory/warehouses/${id}`, data)),
  deleteWarehouse: (id) => bust(['inv:warehouses', 'inv:itemwh'], api.delete(`/inventory/warehouses/${id}`)),
  removeAllWarehouseItems: (id, payload = {}) => bust('inv:itemwh', api.post(`/inventory/warehouses/${id}/remove-all`, payload)),
  seedWarehouseStock: (id, payload) => api.post(`/inventory/warehouses/${id}/seed-stock`, payload),

  // Stock Transfers
  getStockTransfers: (params) => api.get('/inventory/transfers', { params }),
  createStockTransfer: (data) => api.post('/inventory/transfers', data),
  confirmStockTransfer: (id) => api.post(`/inventory/transfers/${id}/confirm`),

  // In-Transit Transfers (InTT)
  getInTransitTransfers: (params) => api.get('/inventory/intransit-transfers', { params }),
  getInTransitTransfer: (id) => api.get(`/inventory/intransit-transfers/${id}`),
  getInTransitSpecialWarehouses: () => api.get('/inventory/intransit-transfers/special-warehouses'),
  createInTransitTransfer: (data) => api.post('/inventory/intransit-transfers', data),
  shipInTransitTransfer: (id) => api.post(`/inventory/intransit-transfers/${id}/ship`),
  receiveInTransitTransfer: (id, payload) => api.post(`/inventory/intransit-transfers/${id}/receive`, payload),
  cancelInTransitTransfer: (id) => api.post(`/inventory/intransit-transfers/${id}/cancel`),

  // Reports
  getWarehouseStock: (params) => api.get('/inventory/reports/stock', { params }),
  // Lightweight on-hand levels: item/warehouse -> quantity only. Use in document
  // forms that just need a stock-availability map (Sales Invoice/Order, GRV, RTS,
  // Credit Note) instead of the heavy /reports/stock report query.
  getWarehouseStockLevels: (params) => api.get('/inventory/reports/stock-levels', { params }),
  getPricingReport: (params) => api.get('/inventory/reports/pricing', { params }),
  getCostingReport: (params) => api.get('/inventory/reports/costing', { params }),
  getInventoryValuation: (params) => api.get('/inventory/reports/valuation', { params }),
  getStockAging: (params) => api.get('/inventory/reports/stock-aging', { params }),
  getNetSalesReport: (params) => api.get('/inventory/reports/net-sales', { params }),
  getFIFOCostingReport: (params) => api.get('/inventory/reports/fifo-costing', { params }),
  getMovementAnalysis: (params) => api.get('/inventory/reports/movement-analysis', { params }),
  getInventoryTurnover: (params) => api.get('/inventory/reports/turnover', { params }),
  getGrossMarginReport: (params) => api.get('/inventory/reports/gross-margin', { params }),
  getSupplierPerformance: (params) => api.get('/inventory/reports/supplier-performance', { params }),
  getSalesVsPurchasesReport: (params) => api.get('/inventory/reports/sales-vs-purchases', { params }),
  getLotStockOnHand: (params) => api.get('/inventory/reports/lot-stock', { params }),
  getExpiryAging: (params) => api.get('/inventory/reports/expiry-aging', { params }),

  // Item-Warehouse Mappings
  getItemWarehouseMappings: () => cachedRequest('inv:itemwh', () => api.get('/inventory/item-warehouses')),

  // Stock Adjustments
  createStockAdjustment: (data) => api.post('/inventory/adjustments', data),

  // Inventory Count
  getCounts: (params) => api.get('/inventory/counts', { params }),
  getCount: (id) => api.get(`/inventory/counts/${id}`),
  createCount: (data) => api.post('/inventory/counts', data),
  updateCount: (id, data) => api.put(`/inventory/counts/${id}`, data),
  deleteCount: (id) => api.delete(`/inventory/counts/${id}`),
  startCount: (id) => api.post(`/inventory/counts/${id}/start`),
  updateCountLine: (countId, lineId, data) => api.put(`/inventory/counts/${countId}/lines/${lineId}`, data),
  calculateCountVariance: (id) => api.post(`/inventory/counts/${id}/variance`),
  recountCountLines: (id, data) => api.post(`/inventory/counts/${id}/recount`, data),
  approveCount: (id) => api.post(`/inventory/counts/${id}/approve`),
  getCountPostingPreview: (id) => api.get(`/inventory/counts/${id}/posting-preview`),
  postCount: (id) => api.post(`/inventory/counts/${id}/post`),
  getCountVarianceReport: (params) => api.get('/inventory/counts/variance-report', { params }),

  // Transaction Enquiry
  getTransactions: (params) => api.get('/inventory/transactions', { params }),

  // Feature Flags
  getFeatureFlags: () => cachedRequest('inv:featureflags', () => api.get('/inventory/feature-flags')),
  updateFeatureFlag: (key, isEnabled) => bust('inv:featureflags', api.put(`/inventory/feature-flags/${key}`, { is_enabled: isEnabled })),

  // Batch / Lot Tracking
  getBatches: (params) => api.get('/inventory/batches', { params }),
  getBatch: (id) => api.get(`/inventory/batches/${id}`),
  getBatchTrace: (id) => api.get(`/inventory/batches/${id}/trace`),
  getBatchRecall: (id) => api.get(`/inventory/batches/${id}/recall`),
  searchLots: (q, limit) => api.get('/inventory/batches/search', { params: { q, limit } }),
  createBatch: (data) => api.post('/inventory/batches', data),
  updateBatch: (id, data) => api.put(`/inventory/batches/${id}`, data),
  updateBatchStatus: (id, status) => api.put(`/inventory/batches/${id}/status`, { status }),
  deleteBatch: (id) => api.delete(`/inventory/batches/${id}`),
  getNearExpiryBatches: (days) => api.get('/inventory/batches/near-expiry', { params: { days } }),
  getExpiredBatches: () => api.get('/inventory/batches/expired'),
  getItemBatches: (itemId, warehouseId, options = {}) => api.get(`/inventory/items/${itemId}/batches`, {
    params: {
      warehouse_id: warehouseId,
      include_empty: options.includeEmpty ? 'true' : undefined,
      only_active: options.onlyActive === false ? 'false' : undefined,
    },
  }),

  // Variant Attributes
  getVariantAttributes: () => api.get('/inventory/variant-attributes'),
  createVariantAttribute: (data) => api.post('/inventory/variant-attributes', data),
  updateVariantAttribute: (id, data) => api.put(`/inventory/variant-attributes/${id}`, data),
  deleteVariantAttribute: (id) => api.delete(`/inventory/variant-attributes/${id}`),
  getAttributeValues: (attrId) => api.get(`/inventory/variant-attributes/${attrId}/values`),
  createAttributeValue: (attrId, data) => api.post(`/inventory/variant-attributes/${attrId}/values`, data),
  updateAttributeValue: (id, data) => api.put(`/inventory/variant-attribute-values/${id}`, data),
  deleteAttributeValue: (id) => api.delete(`/inventory/variant-attribute-values/${id}`),

  // Item Variants
  getItemVariants: (itemId) => api.get(`/inventory/items/${itemId}/variants`),
  getItemVariantConfig: (itemId) => api.get(`/inventory/items/${itemId}/variant-config`),
  generateItemVariants: (itemId, data) => api.post(`/inventory/items/${itemId}/variants/generate`, data),

  // AI Features
  getReorderSuggestions: () => api.get('/inventory/reorder-suggestions'),
  getLastPOSupplier: (itemId) => api.get(`/inventory/items/${itemId}/last-po-supplier`),
};
